import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Client-only preferences for the Classification Review grid.
 *
 * Purely client-side — none of this touches the query/filter state, so
 * changing it fires zero backend requests. Persisted so a reviewer's preferred
 * density and layout survive reloads.
 *
 * NOTE: sort `ordering` lives in the filter/request model (appliedFilters), not
 * here, because it IS sent to the backend and must apply before pagination.
 */
export const MIN_CARDS_PER_ROW = 3;
export const MAX_CARDS_PER_ROW = 12;
export const DEFAULT_CARDS_PER_ROW = 6;
export const DEFAULT_EMPHASIS_LABEL = "boulder";

const clamp = (n: number) =>
  Math.max(MIN_CARDS_PER_ROW, Math.min(MAX_CARDS_PER_ROW, Math.round(n)));

interface ReviewLayoutState {
  cardsPerRow: number;
  setCardsPerRow: (n: number) => void;
  /** Whether to show the dashed "same event" group borders on cards. */
  groupHighlight: boolean;
  setGroupHighlight: (on: boolean) => void;
  /** A label to visually emphasise across the grid (e.g. "boulder"). */
  emphasisLabel: string;
  setEmphasisLabel: (label: string) => void;
  /** Whether label emphasis is active. */
  emphasisEnabled: boolean;
  setEmphasisEnabled: (on: boolean) => void;
  /** Grid layout (density) vs one-event-per-row layout. */
  layoutMode: "grid" | "event-rows";
  setLayoutMode: (mode: "grid" | "event-rows") => void;
}

export const useReviewLayoutStore = create<ReviewLayoutState>()(
  persist(
    (set) => ({
      cardsPerRow: DEFAULT_CARDS_PER_ROW,
      setCardsPerRow: (n) => set({ cardsPerRow: clamp(n) }),
      groupHighlight: true,
      setGroupHighlight: (on) => set({ groupHighlight: on }),
      emphasisLabel: DEFAULT_EMPHASIS_LABEL,
      setEmphasisLabel: (label) => set({ emphasisLabel: label }),
      emphasisEnabled: true,
      setEmphasisEnabled: (on) => set({ emphasisEnabled: on }),
      layoutMode: "grid",
      setLayoutMode: (mode) => set({ layoutMode: mode }),
    }),
    { name: "ml-review-layout" },
  ),
);
